/**
 * 获取高精度浏览器指纹 (32位短指纹，碰撞率 ≤ 1/100,000)
 */
interface FingerprintEnvironment {
  screen: string;
  timezone: string;
  language: string;
  platform: string;
  cores: string;
  userAgent: string;
  dnt: string;
}

interface FingerprintComponents {
  canvasData?: string;
  webglData?: string;
  fontData?: string;
  env?: FingerprintEnvironment;
  error?: string;
}

interface FingerprintResult {
  fingerprint: string;
  components: FingerprintComponents;
}

interface WebGLDebugRendererInfoLike {
  UNMASKED_VENDOR_WEBGL: number;
  UNMASKED_RENDERER_WEBGL: number;
}

export async function getFingerprint(): Promise<FingerprintResult> {
  function getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    const parts = document.cookie.split(';');
    for (const part of parts) {
      const [k, ...rest] = part.trim().split('=');
      if (k === name) return decodeURIComponent(rest.join('='));
    }
    return null;
  }

  function setCookie(name: string, value: string, maxAgeSeconds: number) {
    if (typeof document === 'undefined') return;
    const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}`;
  }

  function isHex32(value: string): boolean {
    return /^[0-9a-f]{32}$/.test(value);
  }

  const cached = getCookie('aifo_visitor_id');
  if (cached && isHex32(cached)) {
    return { fingerprint: cached, components: {} };
  }

  function hash32(str: string): number {
    let hash = 0;
    if (str.length === 0) return 0;
    for (let i = 0; i < str.length; i++) {
      const chr = str.charCodeAt(i);
      hash = (hash << 5) - hash + chr;
      hash |= 0;
    }
    return hash >>> 0;
  }

  function toHex32(value: number): string {
    return value.toString(16).padStart(8, '0');
  }

  function hashCode(str: string): string {
    return toHex32(hash32(str));
  }

  // ---------- Canvas 指纹 ----------
  function getCanvasFingerprint(): string {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 128;
    const ctx = canvas.getContext('2d');
    if (!ctx) return 'canvas_not_supported';
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(0, 0, 256, 128);
    ctx.font = '24px Arial';
    ctx.fillStyle = '#333';
    ctx.shadowColor = '#999';
    ctx.shadowBlur = 8;
    ctx.fillText('Cwm fjordbank glyphs', 20, 50);
    ctx.beginPath();
    ctx.arc(180, 70, 30, 0, Math.PI * 2);
    ctx.fillStyle = '#e74c3c';
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = '#2c3e50';
    ctx.lineWidth = 4;
    ctx.strokeRect(20, 70, 60, 40);
    ctx.beginPath();
    ctx.moveTo(200, 100);
    ctx.bezierCurveTo(220, 60, 240, 120, 250, 80);
    ctx.strokeStyle = '#2ecc71';
    ctx.lineWidth = 3;
    ctx.stroke();
    return canvas.toDataURL('image/png');
  }

  // ---------- WebGL 指纹 ----------
  function getWebGLFingerprint(): string {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) return 'webgl_not_supported';
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info') as WebGLDebugRendererInfoLike | null;
    const renderer = debugInfo ? String(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)) : 'unknown_renderer';
    const vendor = debugInfo ? String(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)) : 'unknown_vendor';
    const version = gl.getParameter(gl.VERSION);
    const shadingLang = gl.getParameter(gl.SHADING_LANGUAGE_VERSION);
    return `${vendor}|${renderer}|${version}|${shadingLang}`;
  }

  // ---------- 字体指纹 ----------
  function getFontFingerprint(): string {
    const testFonts = [
      'Arial',
      'Helvetica',
      'Times New Roman',
      'Courier New',
      'Verdana',
      'Georgia',
      'Comic Sans MS',
      'Trebuchet MS',
      'Impact',
      'Monaco',
      'Menlo',
      'PingFang SC',
      'Microsoft YaHei',
      'WenQuanYi Micro Hei',
      'Noto Sans',
      'Roboto',
      'Open Sans',
    ];
    const baseFont = 'monospace';
    const testStr = 'mmmmmmmmmmlli';
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return 'font_canvas_not_supported';
    canvas.width = 500;
    canvas.height = 100;
    ctx.font = `72px ${baseFont}`;
    const baseWidth = ctx.measureText(testStr).width;
    const detected: string[] = [];
    for (const font of testFonts) {
      ctx.font = `72px "${font}", ${baseFont}`;
      const width = ctx.measureText(testStr).width;
      if (width !== baseWidth) detected.push(font);
    }
    return detected.join(',');
  }

  // ---------- 环境信息 ----------
  function getEnvironmentFingerprint(): FingerprintEnvironment {
    return {
      screen: `${screen.width}x${screen.height}x${screen.colorDepth}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language || 'en-US',
      platform: navigator.platform,
      cores: String(navigator.hardwareConcurrency || 'unknown'),
      userAgent: navigator.userAgent,
      dnt: String(navigator.doNotTrack || 'unknown'),
    };
  }

  // ---------- 组装原始数据 ----------
  function serialize(canvasData: string, webglData: string, fontData: string, env: FingerprintEnvironment): string {
    const canvasHash = hashCode(canvasData);
    const webglHash = hashCode(webglData);
    return [canvasHash, webglHash, fontData, env.screen, env.timezone, env.language, env.platform, env.cores, env.userAgent, env.dnt].join('|||');
  }

  // ---------- SHA-256 并截取前32位 ----------
  async function sha256To32bit(message: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(message);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    return hex.slice(0, 32); // ← 这里截取 32 位
  }

  // ---------- 主流程 ----------
  try {
    const canvasData = getCanvasFingerprint();
    const webglData = getWebGLFingerprint();
    const fontData = getFontFingerprint();
    const env = getEnvironmentFingerprint();
    const serialized = serialize(canvasData, webglData, fontData, env);

    if (window.crypto && window.crypto.subtle) {
      const fingerprint = await sha256To32bit(serialized);
      setCookie('aifo_visitor_id', fingerprint, 60 * 60 * 24 * 365 * 2);
      return { fingerprint, components: { canvasData: canvasData.slice(0, 50) + '...', webglData, fontData, env } };
    } else {
      // 降级返回 32位字符串（补齐）
      const fp = [
        toHex32(hash32(serialized)),
        toHex32(hash32(`${serialized}|1`)),
        toHex32(hash32(`${serialized}|2`)),
        toHex32(hash32(`${serialized}|3`)),
      ].join('');
      setCookie('aifo_visitor_id', fp, 60 * 60 * 24 * 365 * 2);
      return { fingerprint: fp, components: { canvasData: canvasData.slice(0, 50) + '...', webglData, fontData, env } };
    }
  } catch (e: unknown) {
    const fallback = navigator.userAgent + screen.width + screen.height + navigator.language;
    const fp = [
      toHex32(hash32(fallback)),
      toHex32(hash32(`${fallback}|1`)),
      toHex32(hash32(`${fallback}|2`)),
      toHex32(hash32(`${fallback}|3`)),
    ].join('');
    setCookie('aifo_visitor_id', fp, 60 * 60 * 24 * 365 * 2);
    const errorMessage = e instanceof Error ? e.message : String(e);
    return { fingerprint: fp, components: { error: errorMessage } };
  }
}
